import { HazardKind, ShapeKind } from '../sim/traits';
import type { HazardVisual } from './hazardShapes';
import type { ThemePalette } from '../sim/level/themes';
import { group, Layer, type ShapeGroup } from './frame';
import { PRIM_CAPSULE, PRIM_DISK, PRIM_ROUNDED_BOX, type Primitive, shade, withAlpha } from './sdf/primitives';

const cap = (ax: number, ay: number, bx: number, by: number, r: number): Primitive => ({ kind: PRIM_CAPSULE, ax, ay, bx, by, r });
const disk = (ax: number, ay: number, r: number): Primitive => ({ kind: PRIM_DISK, ax, ay, bx: ax, by: ay, r });

/** An expanded silhouette behind the art keeps its original physical contour readable. */
export function inkOutline(primitives: Primitive[], thickness: number, layer: number, color = '#151d29'): ShapeGroup {
  return group(primitives.map((p) => p.kind === PRIM_ROUNDED_BOX
    ? { ...p, bx: p.bx + thickness, by: p.by + thickness, r: p.r + thickness }
    : { ...p, r: p.r + thickness }), color, layer, { blend: 'smoothUnion', smoothK: 0.04, style: 'flat', pad: thickness + 0.1 });
}

export type ShadowSurface = { x: number; y: number; angle: number; hx: number; hy: number; r: number; kind: number };
export const SHADOW_CASTERS = new Set<number>([
  HazardKind.Solid, HazardKind.Destructible, HazardKind.Ice, HazardKind.Conveyor,
  HazardKind.MovingPlatform, HazardKind.RotatingPlatform, HazardKind.Collapsing, HazardKind.Momentum,
]);

/** Intersect a vertical line with the rendered fixture, including tilted ledges. */
export function surfaceHeight(surface: ShadowSurface, x: number): number | null {
  if (surface.kind === ShapeKind.Circle) {
    const dx = x - surface.x;
    return Math.abs(dx) < surface.r ? surface.y + Math.sqrt(surface.r ** 2 - dx ** 2) : null;
  }
  const c = Math.cos(surface.angle), s = Math.sin(surface.angle);
  const points = [[-surface.hx, -surface.hy], [surface.hx, -surface.hy], [surface.hx, surface.hy], [-surface.hx, surface.hy]]
    .map(([px, py]) => ({ x: surface.x + px! * c - py! * s, y: surface.y + px! * s + py! * c }));
  let top = -Infinity;
  for (let i = 0; i < 4; i++) {
    const a = points[i]!, b = points[(i + 1) % 4]!;
    if (Math.abs(a.x - b.x) < 0.0001 || x < Math.min(a.x, b.x) || x > Math.max(a.x, b.x)) continue;
    top = Math.max(top, a.y + (b.y - a.y) * (x - a.x) / (b.x - a.x));
  }
  return Number.isFinite(top) ? top : null;
}

export function contactShadow(surfaces: ShadowSurface[], x: number, feetY: number, radius: number): ShapeGroup[] {
  let landing: ShadowSurface | undefined, top = -Infinity;
  for (const surface of surfaces) {
    const y = surfaceHeight(surface, x);
    if (y !== null && y <= feetY + 0.25 && y > top) { top = y; landing = surface; }
  }
  const height = feetY - top;
  if (!landing || height > 7) return [];
  const width = radius * (1 + Math.max(0, height) * 0.045);
  // Sample the receiving surface at both ends so shadows cannot float across a ledge edge.
  const left = surfaceHeight(landing, x - width), right = surfaceHeight(landing, x + width);
  if (left === null || right === null) return [];
  const opacity = 0.32 * (1 - Math.max(0, height) / 8);
  return [
    group([cap(x - width, left + 0.025, x + width, right + 0.025, 0.085)], withAlpha('#101622', opacity * 0.35), 4.5, { style: 'flat', pad: 0.1 }),
    group([cap(x - width * 0.8, left + 0.035, x + width * 0.8, right + 0.035, 0.04)], withAlpha('#101622', opacity), 4.5, { style: 'flat', pad: 0.1 }),
  ];
}

/** Recessed supports use the backdrop palette; bright top caps remain the playable surface. */
export function platformDetails(v: HazardVisual, theme: ThemePalette): ShapeGroup[] {
  const { hx, hy } = v.shape;
  if (hx < 0.8 || Math.abs(v.angle) > 0.02) return [];
  const out: ShapeGroup[] = [], details: Primitive[] = [];
  const x = v.x, bottom = v.y - hy, top = v.y + hy;
  const count = Math.min(8, Math.max(2, Math.floor(hx)));
  if (hy >= 0.65) {
    for (let i = 0; i < count; i++) {
      const px = x - hx + (i + 0.5) * hx * 2 / count;
      details.push(cap(px, bottom + 0.15, px + (theme.id === 'woods' ? 0.25 : 0), top - 0.25, 0.022));
    }
    if (theme.id === 'castle') details.push(cap(x - hx + 0.1, v.y, x + hx - 0.1, v.y, 0.025));
    out.push(group(details, shade(theme.solid, 0.13), Layer.Solids, { style: 'flat' }));
    return out;
  }
  if (theme.id === 'woods' || theme.id === 'winter') {
    details.push(cap(x - hx * 0.86, bottom - 0.08, x + hx * 0.86, bottom - 0.08, 0.15));
    for (const side of [-1, 1]) {
      details.push(cap(x + side * hx * 0.7, bottom, x + side * hx * 0.32, bottom - 0.7, 0.1));
      details.push(cap(x + side * hx * 0.32, bottom - 0.7, x + side * hx * 0.45, bottom - 1.3, 0.055));
    }
  } else if (theme.id === 'factory' || theme.id === 'laser') {
    details.push(cap(x - hx * 0.8, bottom - 0.6, x + hx * 0.8, bottom - 0.6, 0.07));
    for (let i = 0; i < Math.min(count, 5); i++) {
      const px = x - hx * 0.8 + i * hx * 1.6 / Math.min(count, 5);
      details.push(cap(px, bottom - 0.05, px + hx * 1.6 / Math.min(count, 5), bottom - 0.6, 0.055));
    }
    out.push(group([-1, 1].map((s) => disk(x + s * hx * 0.85, bottom - 0.22, 0.07)), '#e7b45b', Layer.Decor, { style: 'flat' }));
  } else if (theme.id === 'castle') {
    for (let i = 0; i < 8; i++) {
      const a = Math.PI * i / 8, b = Math.PI * (i + 1) / 8;
      details.push(cap(x + Math.cos(a) * hx * 0.82, bottom - 1.65 + Math.sin(a) * 1.3, x + Math.cos(b) * hx * 0.82, bottom - 1.65 + Math.sin(b) * 1.3, 0.13));
    }
  } else return [];
  out.push(group(details, withAlpha(theme.decor, 0.85), Layer.Decor, { style: 'flat' }));
  return out;
}

export function arrivalGroups(x: number, y: number, color: string, protection: number): ShapeGroup[] {
  const life = Math.min(1, protection / 45);
  return [
    group([disk(x, y, 1.04)], withAlpha(color, 0.07), Layer.Overlay, { style: 'flat' }),
    group([disk(x, y, 1.04)], withAlpha('#e7fbff', 0.3 + life * 0.55), Layer.Overlay, { style: 'outline', pad: 0.15 }),
    group([cap(x - 0.48, y + 1.25, x - 0.48 + life * 0.96, y + 1.25, 0.05)], '#e7fbff', Layer.Overlay, { style: 'flat' }),
  ];
}

export function victoryGroups(x: number, y: number, color: string, elapsed: number): ShapeGroup[] {
  if (elapsed < 0 || elapsed > 1.8) return [];
  const shapes: Primitive[] = [];
  for (let i = 0; i < 12; i++) {
    const a = Math.PI * (0.12 + i * 0.76 / 11), speed = 2.2 + i % 3;
    const px = x + Math.cos(a) * speed * elapsed;
    const py = y + 1.2 + Math.sin(a) * speed * elapsed - 2.3 * elapsed * elapsed;
    shapes.push(cap(px, py, px + Math.sin(i + elapsed * 5) * 0.1, py + 0.09, 0.028));
  }
  return [group(shapes, withAlpha(color, Math.min(1, (1.8 - elapsed) * 2)), Layer.Overlay, { style: 'flat' })];
}
